import React, { useState, useEffect, useMemo } from 'react';
import SeoPanel from '../../components/SeoPanel';
import { analyseSeo } from '../../utils/seoAnalysis';
import toast from 'react-hot-toast';
import {
  Plus, Edit2, Trash2, Save, X, Loader2, Zap,
  Calendar, DollarSign, Heart, Bot, MapPin, FileText, AlertTriangle,
  Clock, Users, Shield, BarChart3, Pill, Globe, Lock, Smartphone,
  Brain, Activity, ChevronUp, ChevronDown, Search, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';
import ImageUploadField from '../../components/ImageUploadField';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

// ── Icon registry ──────────────────────────────────────────────────────────
const ICON_MAP = {
  Calendar, DollarSign, Heart, Bot, MapPin, FileText, AlertTriangle,
  Clock, Users, Zap, Shield, BarChart3, Pill, Globe, Lock, Smartphone,
  Brain, Activity,
};
const ICON_NAMES = Object.keys(ICON_MAP);

function FeatureIcon({ name, size = 16, className }) {
  const Comp = ICON_MAP[name] || Zap;
  return <Comp size={size} className={className} />;
}

// ── Tier helpers ───────────────────────────────────────────────────────────
const TIER_LABELS = { 1: 'Core', 2: 'Supporting', 3: 'Extra' };
const TIER_CLASSES = {
  1: 'bg-amber-100 text-amber-700 border border-amber-200',
  2: 'bg-indigo-100 text-indigo-700 border border-indigo-200',
  3: 'bg-slate-100 text-slate-600 border border-slate-200',
};
const TIER_DOT = {
  1: 'bg-amber-400',
  2: 'bg-indigo-400',
  3: 'bg-slate-400',
};

// ── Icon gradient colours (cycles) ────────────────────────────────────────
const ICON_GRADIENTS = [
  'from-teal-500 to-cyan-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-500',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-green-600',
  'from-pink-500 to-rose-600',
  'from-red-500 to-orange-600',
  'from-sky-500 to-blue-600',
];

// ──────────────────────────────────────────────────────────────────────────
// MODAL
// ──────────────────────────────────────────────────────────────────────────
// Normalise a benefits/faqs value (may arrive as an array, JSON string, or null)
// into a pretty-printed JSON string for editing in a <textarea>.
function toJsonText(value) {
  if (value == null || value === '') return '[]';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value; // leave raw text so the user can fix it
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[]';
  }
}

function FeatureModal({ feature, onClose, onSaved }) {
  const isEdit = Boolean(feature?.id);
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title:            feature?.title            || '',
    subtitle:         feature?.subtitle         || '',
    description:      feature?.description      || '',
    long_description: feature?.long_description || '',
    icon:             feature?.icon             || 'Zap',
    image_url:        feature?.image_url        || '',
    category:         feature?.category         || 'General',
    tier:             feature?.tier             ?? 1,
    badge:            feature?.badge            || '',
    sort_order:       feature?.sort_order       ?? 0,
    is_active:        feature?.is_active        !== false,
    // ── Dedicated feature-page fields ──
    slug:             feature?.slug             || '',
    hero_subtitle:    feature?.hero_subtitle    || '',
    body:             feature?.body             || '',
    seo_title:        feature?.seo_title        || '',
    seo_description:  feature?.seo_description  || '',
    benefits:         toJsonText(feature?.benefits),
    faqs:             toJsonText(feature?.faqs),
    // Same SEO field set as blog and pages. This table names its meta columns
    // seo_title/seo_description, which the panel is told about rather than renamed.
    focus_keyword:      feature?.focus_keyword || '',
    secondary_keywords: Array.isArray(feature?.secondary_keywords)
                          ? feature.secondary_keywords.join(', ')
                          : (feature?.secondary_keywords || ''),
    canonical_url:      feature?.canonical_url || '',
    robots_index:       feature?.robots_index !== false,
    robots_follow:      feature?.robots_follow !== false,
    og_title:           feature?.og_title || '',
    og_description:     feature?.og_description || '',
    og_image:           feature?.og_image || '',
    twitter_title:       feature?.twitter_title || '',
    twitter_description: feature?.twitter_description || '',
    twitter_image:       feature?.twitter_image || '',
    schema_type:        feature?.schema_type || 'Service',
    breadcrumb_title:   feature?.breadcrumb_title || '',
    is_pillar:          !!feature?.is_pillar,
  });

  const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));
  const setCheck = field => e => setForm(f => ({ ...f, [field]: e.target.checked }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }

    // The panel edits secondary keywords as a comma string; the API stores an array.
    const secondaryKw = String(form.secondary_keywords || '')
      .split(',').map(t => t.trim()).filter(Boolean);

    // Parse the JSON-text array fields (benefits, faqs) with validation
    let benefits;
    let faqs;
    try {
      benefits = form.benefits.trim() ? JSON.parse(form.benefits) : [];
      if (!Array.isArray(benefits)) throw new Error('Benefits must be a JSON array');
    } catch (err) {
      toast.error(`Benefits: invalid JSON — ${err.message}`);
      return;
    }
    try {
      faqs = form.faqs.trim() ? JSON.parse(form.faqs) : [];
      if (!Array.isArray(faqs)) throw new Error('FAQs must be a JSON array');
    } catch (err) {
      toast.error(`FAQs: invalid JSON — ${err.message}`);
      return;
    }

    setSaving(true);
    try {
      const url = isEdit
        ? `${API}/api/marketing/features/${feature.id}`
        : `${API}/api/marketing/features`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          tier:       parseInt(form.tier)       || 1,
          sort_order: parseInt(form.sort_order) || 0,
          image_url:  form.image_url || null,
          slug:       form.slug || null,
          benefits,
          faqs,
          secondary_keywords: secondaryKw,
          seo_score: analyseSeo({
            title: form.title,
            metaTitle: form.seo_title,
            metaDescription: form.seo_description,
            slug: form.slug,
            content: form.body || form.long_description,
            excerpt: form.hero_subtitle || form.description,
            focusKeyword: form.focus_keyword,
            secondaryKeywords: secondaryKw,
            featuredImage: form.image_url,
          }).score,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Save failed');
      toast.success(isEdit ? 'Feature updated' : 'Feature created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  const PreviewIcon = ICON_MAP[form.icon] || Zap;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Feature' : 'New Feature'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">

          {/* Title + Subtitle */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Title *</label>
              <input type="text" value={form.title} onChange={set('title')} required
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Subtitle</label>
              <input type="text" value={form.subtitle} onChange={set('subtitle')}
                placeholder="Short tagline shown under title"
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
            <textarea value={form.description} onChange={set('description')} rows={3} placeholder="Short description shown on the features grid"
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-none" />
          </div>

          {/* Long Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Feature Detail / Bullets
              <span className="text-slate-400 font-normal ml-1.5">(one per line)</span>
            </label>
            <textarea value={form.long_description} onChange={set('long_description')} rows={4}
              placeholder={"• Automated scheduling\n• Real-time updates\n• NDIS compliant"}
              className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-none font-mono" />
          </div>

          {/* Icon Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Icon</label>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shrink-0">
                <PreviewIcon size={18} className="text-white" />
              </div>
              <p className="text-sm text-slate-500">Selected: <span className="font-semibold text-slate-700">{form.icon}</span></p>
            </div>
            <div className="grid grid-cols-9 gap-1.5 p-3 bg-slate-50 rounded-xl border border-slate-200">
              {ICON_NAMES.map(name => {
                const Comp = ICON_MAP[name];
                const active = form.icon === name;
                return (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => setForm(f => ({ ...f, icon: name }))}
                    className={clsx(
                      'w-8 h-8 rounded-lg flex items-center justify-center transition-all',
                      active
                        ? 'bg-teal-500 text-white shadow-sm scale-110'
                        : 'text-slate-400 hover:bg-white hover:text-slate-700 hover:shadow-sm'
                    )}
                  >
                    <Comp size={15} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Feature image upload */}
          <ImageUploadField
            label="Feature image (optional)"
            value={form.image_url || ''}
            onChange={(url) => setForm(f => ({ ...f, image_url: url }))}
            hint="Optional: shown in the feature detail or split layout"
          />

          {/* Category + Badge + Sort */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Category</label>
              <input type="text" value={form.category} onChange={set('category')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Badge</label>
              <input type="text" value={form.badge} onChange={set('badge')} placeholder="NEW"
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sort Order</label>
              <input type="number" value={form.sort_order} onChange={set('sort_order')} min={0}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
          </div>

          {/* Tier */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tier</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 1, label: 'Tier 1 — Core',        cls: 'border-amber-300 bg-amber-50 text-amber-800'  },
                { v: 2, label: 'Tier 2 — Supporting',  cls: 'border-indigo-300 bg-indigo-50 text-indigo-800' },
                { v: 3, label: 'Tier 3 — Extra',       cls: 'border-slate-300 bg-slate-50 text-slate-700'  },
              ].map(({ v, label, cls }) => (
                <label key={v}
                  className={clsx(
                    'flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all text-sm font-medium',
                    parseInt(form.tier) === v ? cls : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                  )}
                >
                  <input type="radio" name="tier" value={v}
                    checked={parseInt(form.tier) === v}
                    onChange={set('tier')}
                    className="accent-primary-600" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3 pt-1">
            <input type="checkbox" id="feat_active" checked={form.is_active} onChange={setCheck('is_active')}
              className="w-4 h-4 accent-teal-600 rounded" />
            <label htmlFor="feat_active" className="text-sm font-medium text-slate-700">Active (visible on marketing site)</label>
          </div>

          {/* ── Dedicated Feature Page ───────────────────────────────────── */}
          <div className="pt-4 mt-2 border-t border-slate-100">
            <h3 className="text-sm font-bold text-slate-900 mb-1">Dedicated Feature Page</h3>
            <p className="text-xs text-slate-400 mb-4">Content for the standalone /features/&lt;slug&gt; page.</p>

            <div className="space-y-4">
              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Slug
                  <span className="text-slate-400 font-normal ml-1.5">(URL: /features/&lt;slug&gt;)</span>
                </label>
                <input type="text" value={form.slug} onChange={set('slug')} placeholder="e.g. smart-rostering"
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
              </div>

              {/* Hero subtitle */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Hero Subtitle</label>
                <input type="text" value={form.hero_subtitle} onChange={set('hero_subtitle')}
                  placeholder="Subtitle shown in the feature-page hero"
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
              </div>

              {/* Body (rich HTML) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Feature page content (HTML)
                  <span className="text-slate-400 font-normal ml-1.5">(HTML allowed)</span>
                </label>
                <textarea value={form.body} onChange={set('body')} rows={8}
                  placeholder={'<h2>How it works</h2>\n<p>Deep description of the feature…</p>'}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-y font-mono" />
              </div>

              {/* Benefits (JSON) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Benefits
                  <span className="text-slate-400 font-normal ml-1.5">(JSON array of {'{ title, description }'})</span>
                </label>
                <textarea value={form.benefits} onChange={set('benefits')} rows={6}
                  placeholder={'[\n  { "title": "Save time", "description": "Automate rostering." }\n]'}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-y font-mono" />
              </div>

              {/* FAQs (JSON) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  FAQs
                  <span className="text-slate-400 font-normal ml-1.5">(JSON array of {'{ q, a }'})</span>
                </label>
                <textarea value={form.faqs} onChange={set('faqs')} rows={6}
                  placeholder={'[\n  { "q": "Is it NDIS compliant?", "a": "Yes." }\n]'}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-y font-mono" />
              </div>

              {/* SEO — the full analysis panel, same component as the blog editor. */}
              <SeoPanel
                kind="feature"
                urlPrefix="/features/"
                titleField="seo_title"
                descField="seo_description"
                title={form.title}
                content={form.body || form.long_description}
                excerpt={form.hero_subtitle || form.description}
                featuredImage={form.image_url}
                value={form}
                onChange={next => setForm(next)}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-all disabled:opacity-60 shadow-sm">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Feature'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ──────────────────────────────────────────────────────────────────────────
export default function Features() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [tierFilter, setTierFilter] = useState('all');
  const token = localStorage.getItem('token');

  async function fetchFeatures() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/features/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setFeatures(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchFeatures(); }, []);

  async function deleteFeature(id, title) {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      const res = await fetch(`${API}/api/marketing/features/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Feature deleted');
      fetchFeatures();
    } catch {
      toast.error('Failed to delete feature');
    }
  }

  async function toggleActive(feature) {
    try {
      await fetch(`${API}/api/marketing/features/${feature.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...feature, is_active: !feature.is_active }),
      });
      fetchFeatures();
    } catch {
      toast.error('Failed to update feature');
    }
  }

  async function reorder(feature, direction) {
    // find adjacent item in same direction
    const sorted = [...features].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(f => f.id === feature.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const other = sorted[swapIdx];
    try {
      // swap sort_order values
      await Promise.all([
        fetch(`${API}/api/marketing/features/${feature.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...feature, sort_order: other.sort_order }),
        }),
        fetch(`${API}/api/marketing/features/${other.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...other, sort_order: feature.sort_order }),
        }),
      ]);
      fetchFeatures();
    } catch {
      toast.error('Reorder failed');
    }
  }

  // Derived data
  const categories = useMemo(() => {
    const cats = [...new Set(features.map(f => f.category).filter(Boolean))];
    return ['All', ...cats.sort()];
  }, [features]);

  const filtered = useMemo(() => {
    return features
      .filter(f => {
        if (catFilter !== 'All' && f.category !== catFilter) return false;
        if (tierFilter !== 'all' && String(f.tier) !== tierFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          return (
            f.title?.toLowerCase().includes(q) ||
            f.subtitle?.toLowerCase().includes(q) ||
            f.category?.toLowerCase().includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [features, catFilter, tierFilter, search]);

  const activeCount = features.filter(f => f.is_active).length;

  return (
    <div className="p-6 space-y-5">
      {modal && (
        <FeatureModal
          feature={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={fetchFeatures}
        />
      )}

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">Features</h1>
          <span className="px-2.5 py-0.5 bg-teal-100 text-teal-700 text-sm font-bold rounded-full border border-teal-200">
            {features.length}
          </span>
          {!loading && (
            <span className="text-sm text-slate-400">{activeCount} active</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/#features`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl text-sm font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all shadow-sm">
            <Plus size={16} />
            Add Feature
          </button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
        {/* Search */}
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search features..."
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 bg-slate-50"
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-slate-500 mr-1">Category:</span>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCatFilter(cat)}
              className={clsx(
                'px-3 py-1 rounded-full text-xs font-semibold transition-all',
                catFilter === cat
                  ? 'bg-teal-500 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              )}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Tier Tabs */}
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-slate-500 mr-1">Tier:</span>
          {[
            { v: 'all', label: 'All Tiers' },
            { v: '1',   label: 'Core (Tier 1)' },
            { v: '2',   label: 'Supporting (Tier 2)' },
            { v: '3',   label: 'Extras (Tier 3)' },
          ].map(t => (
            <button
              key={t.v}
              onClick={() => setTierFilter(t.v)}
              className={clsx(
                'px-3 py-1 rounded-full text-xs font-semibold transition-all',
                tierFilter === t.v
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={32} className="animate-spin text-teal-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Zap size={40} className="mb-3 opacity-20" />
            <p className="font-medium">{features.length === 0 ? 'No features yet' : 'No results match your filters'}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 w-14">Icon</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Feature</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Category</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Tier</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Order</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((f, i) => {
                const gradClass = ICON_GRADIENTS[i % ICON_GRADIENTS.length];
                const tier = parseInt(f.tier) || 1;
                return (
                  <tr key={f.id} className="hover:bg-slate-50/60 transition-colors group">
                    {/* Icon */}
                    <td className="px-5 py-3.5">
                      <div className={clsx('w-9 h-9 rounded-xl bg-gradient-to-br flex items-center justify-center', gradClass)}>
                        <FeatureIcon name={f.icon} size={16} className="text-white" />
                      </div>
                    </td>

                    {/* Title + Subtitle */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                            {f.title}
                            {f.badge && (
                              <span className="text-[10px] font-bold bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-md">
                                {f.badge}
                              </span>
                            )}
                          </p>
                          {f.subtitle && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{f.subtitle}</p>}
                        </div>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full border border-slate-200">
                        {f.category || '—'}
                      </span>
                    </td>

                    {/* Tier */}
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      <span className={clsx('text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-fit', TIER_CLASSES[tier])}>
                        <span className={clsx('w-1.5 h-1.5 rounded-full', TIER_DOT[tier])} />
                        Tier {tier} — {TIER_LABELS[tier]}
                      </span>
                    </td>

                    {/* Active Toggle */}
                    <td className="px-4 py-3.5">
                      <button
                        onClick={() => toggleActive(f)}
                        className={clsx(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all',
                          f.is_active
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200'
                        )}
                      >
                        <span className={clsx('w-1.5 h-1.5 rounded-full', f.is_active ? 'bg-emerald-500' : 'bg-slate-400')} />
                        {f.is_active ? 'Active' : 'Hidden'}
                      </button>
                    </td>

                    {/* Reorder */}
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      <div className="flex items-center justify-center gap-0.5">
                        <span className="text-xs text-slate-400 font-mono mr-1 w-5 text-center">{f.sort_order}</span>
                        <button
                          onClick={() => reorder(f, 'up')}
                          className="p-1 rounded text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                          title="Move up"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          onClick={() => reorder(f, 'down')}
                          className="p-1 rounded text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                          title="Move down"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setModal(f)}
                          className="p-2 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => deleteFeature(f.id, f.title)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer count */}
      {!loading && filtered.length > 0 && (
        <p className="text-xs text-slate-400 text-center">
          Showing {filtered.length} of {features.length} features
        </p>
      )}
    </div>
  );
}
